import useTranslation from 'next-translate/useTranslation';
import { ContestStatus, ContestType } from '@betterpool/api-types/contests-service';
import { useRouter } from 'next/router';
import { Group } from '@mantine/core';
import { useMemo } from 'react';

import repeat from '~/common/utils/repeat';
import type { Entry } from '~/domains/contest/domains/game/domains/common/types/entry';
import { PickVariant } from '~/domains/contest/domains/game/domains/common/types/single.pick';
import type { UserEntry } from '~/domains/contest/domains/services/interfaces/user-entry.interface';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import type { TeamPick } from '~/domains/contest/domains/services/interfaces/team-picks.interface';
import { Match } from '~/domains/common/components/Match/Match';
import { Routes } from '~/domains/common/constants/routes';
import type { Slate } from '~/domains/games/interfaces/slates.interface';
import { useIsEntryWithdrawalAllowed } from '~/domains/contest/domains/common/hooks/useIsEntryWithdrawalAllowed';
import { getPicksCount } from '~/domains/contest/domains/common/utils/getPicksCount';

import { getSingleSlates } from '../../../../../game/domains/common/utils/getSingleSlates';
import { GamePick } from '../../../../../game/domains/common/components/GamePick/GamePick';
import { Pick } from '../../../../../game/domains/common/components/Pick/Pick';
import EntryRow from '../Entry/EntryRow';

type MyEntriesContentProps = {
  entries?: UserEntry[];
  fullContest: FullContest;
  defaultSlate: Slate;
  teamPicks: TeamPick;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

function MyEntriesContent({
  fullContest,
  teamPicks,
  defaultSlate,
  entries = [],
  openEntryNameEditModal,
}: MyEntriesContentProps) {
  const { t } = useTranslation('contest');
  const router = useRouter();

  const contestType = fullContest?.contest_type;
  const contestStatus = fullContest?.status;
  const isScheduled = contestStatus === ContestStatus.SCHEDULED;
  const sport = fullContest?.league?.sport;

  const { isEntryWithdrawalAllowed } = useIsEntryWithdrawalAllowed({ fullContest });

  const userEntries: Entry[] = useMemo(
    () =>
      entries.map((entry) => {
        const { singleSlates, currentSingleSlate } = getSingleSlates({
          slates: fullContest?.slates,
          currentTeamPick: teamPicks?.[entry.id],
          currentUserEntry: entry,
          settings: fullContest?.settings,
          leagueAlias: fullContest?.league?.alias,
          selectedSlateId: defaultSlate?.id,
        });
        return {
          id: entry.id,
          name: entry.name,
          singleSlates,
          currentSingleSlate,
          isEmpty: !teamPicks?.[entry.id],
        };
      }),
    [defaultSlate?.id, entries, fullContest, teamPicks]
  );

  const getPicksheetHref = (entry: Entry, slateId?: string) =>
    Routes.gamecenterPicks(fullContest?.id, {
      entryId: entry.id,
      slateId,
    });

  return (
    <>
      {userEntries.map((entry) => (
        <EntryRow
          key={entry.id}
          name={entry.name}
          entryId={entry.id}
          contestId={fullContest?.id}
          isEntertainmentOnly={fullContest?.settings?.entertainmentOnlySettings?.enabled}
          options={{
            editName: {
              title: t('detail.entries.entry.option.editName'),
              onClick: () => openEntryNameEditModal({ id: entry.id, name: entry.name }),
            },
            edit: {
              title: entry.isEmpty
                ? t('detail.entries.entry.option.make')
                : t('detail.entries.entry.option.edit'),
              href: getPicksheetHref(entry),
            },
            withdrawal: {
              title: t('detail.entries.entry.option.withdraw'),
              isHidden: !isEntryWithdrawalAllowed,
            },
          }}
        >
          <Match value={contestType}>
            <Match.On value={[ContestType.TEAM_SURVIVOR]}>
              <Group noWrap spacing={4}>
                {entry.singleSlates.map((singleSlate, index) => (
                  <Pick
                    key={singleSlate.id}
                    onSlateClick={(slateId) => {
                      router.push(getPicksheetHref(entry, slateId));
                    }}
                    shouldMakePicks={
                      isScheduled &&
                      teamPicks?.[entry.id]?.picks &&
                      !Object.keys(teamPicks?.[entry.id]?.picks).length &&
                      index === 0
                      && entry?.currentSingleSlate?.isGenerated
                    }
                    variant={PickVariant.MY_ENTRIES}
                    {...singleSlate}
                  />
                ))}
              </Group>
            </Match.On>

            <Match.On value={[ContestType.TEAM_PICK_EM]}>
              <Group noWrap spacing={4}>
                {entry?.currentSingleSlate?.picks?.map((pick) => (
                  <GamePick
                    key={pick.teamId}
                    {...pick}
                    onGameClick={() => {
                      router.push(getPicksheetHref(entry));
                    }}
                  />
                ))}

                {repeat(getPicksCount(entry?.currentSingleSlate), (index) => (
                  <GamePick
                    key={index}
                    shouldMakePicks={
                      isScheduled &&
                      teamPicks?.[entry.id]?.picks &&
                      !Object.keys(teamPicks?.[entry.id]?.picks).length &&
                      index === 0
                      && entry?.currentSingleSlate?.isGenerated
                    }
                    onGameClick={() => {
                      router.push(getPicksheetHref(entry));
                    }}
                  />
                ))}
              </Group>
            </Match.On>
          </Match>
        </EntryRow>
      ))}
    </>
  );
}

export default MyEntriesContent;
